/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderUserContext,
  FormState,
  PrinterBusinessService,
  PrinterFinderListItem,
  REPORT_JOB_PROGRESS,
  ReportService,
  whenCreateMode,
} from '@gh/builder-shared';
import { BuilderPermission, SystemRoleBit } from '@gh/builder-shared-data';
import { createDialogRoute, FormStateBuilder, getDialogInitData } from '@gh/common-shared';
import { RouteDataService } from '@gh/core-data';
import { LocalizationService, TranslationService } from '@gh/core-mls';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  FormMode,
  getControl,
  getControlValue,
  newFormControl,
  NotificationService,
  setControlValue,
  WaitingService,
} from '@gh/core-ui';
import { GridDefinition, GridDefinitionFactory, newGridPaginationCursor } from '@gh/core-ui-grid';
import { isNotNil } from '@gh/core-util';

import { eq, some } from 'lodash';

import {
  ScheduleJobProgressFilter,
  ScheduleJobProgressFilterDialogConfig,
  ScheduleJobProgressFilterSupervisorType,
  ScheduleJobProgressUserAutocompleteItem,
} from '../../resources/schedule-details.model';
import { ScheduleListBusinessService } from '../../resources/schedule-list-business.service';
import { jobProgressReportFilterToDto } from '../../resources/schedule-list.model';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { GoogleAuthService } from '@gh/core-external';

@Component({
  standalone: false,
  selector: 'gh-schedule-job-progress-filter-dialog',
  templateUrl: 'schedule-job-progress-filter-dialog.component.html',
  styleUrls: ['schedule-job-progress-filter-dialog.component.scss'],
})
export class ScheduleJobProgressFilterDialogComponent implements OnInit, DialogInput<ScheduleJobProgressFilterDialogConfig> {
  input: ScheduleJobProgressFilterDialogConfig;
  form: FormState<ScheduleJobProgressFilter>;
  printerName = newFormControl('');
  printerList: PrinterFinderListItem[];
  originalPrinterList: PrinterFinderListItem[];
  printerGridDefinition: GridDefinition;
  cursor = newGridPaginationCursor();
  scheduleJobProgressFilterSupervisorType = ScheduleJobProgressFilterSupervisorType;
  isSupplierPrinterSelectionActiveHash: Hash<boolean>;
  isCloud: boolean;

  constructor(@Inject(MAT_DIALOG_DATA) private dialogData: any,
              private dialogRef: MatDialogRef<any>,
              private route: ActivatedRoute,
              private routeData: RouteDataService,
              private googleAuth: GoogleAuthService,
              private waitingService: WaitingService,
              private notificationService: NotificationService,
              private formStateBuilder: FormStateBuilder,
              private userContext: BuilderUserContext,
              private localizationService: LocalizationService,
              private gridDefinitionFactory: GridDefinitionFactory,
              private business: ScheduleListBusinessService,
              private reports: ReportService,
              private printerBusinessService: PrinterBusinessService,
              private waitings: WaitingService,
              private translations: TranslationService) {
  }

  get hasPrinters(): boolean {
    return this.printerList.length > 0;
  }

  get isDeliverDisabled(): boolean {
    return !this.isSendSuppliersSelected() || !this.isJobProgressReportAllowed()
      || (this.isSystemRoleMasterOrCorporateOrFranchisee() && !this.isSupervisorSectionCompleted())
      || (this.isSystemRoleSupervisor() && !this.isSupervisorSelected)
      || (this.isSendSuppliersSelected() && this.isPrinterSectionActive && !this.isPrinterSelected());
  }

  get isPrinterSectionVisible(): boolean {
    return this.isCloud && (this.isSendSuppliersSelected()
      && (this.isSystemRoleMasterOrCorporateOrFranchisee()
        ? this.isSupervisorSectionCompleted()
        : this.isSystemRoleSupervisor())
      && this.isPrinterSectionActive);
  }

  get isPrinterSectionActive(): boolean {
    if (this.isSingleSupervisorSelected && this.isSupervisorSelected && this.supervisorControlValue) {
      return this.isSupplierPrinterSelectionActiveHash[this.supervisorControlValue.userId];
    }
    if (this.isAllSupervisorSelected()) {
      return some(this.isSupplierPrinterSelectionActiveHash, (isOnePrinterExist: boolean) => isOnePrinterExist);
    }
    if (this.isSystemRoleSupervisor()) {
      return this.isSupplierPrinterSelectionActiveHash[this.currentSupervisorId()];
    }
    return false;
  }

  get isSingleSupervisorSelected(): boolean {
    return getControlValue(this.form.group, 'supervisorType')
      === ScheduleJobProgressFilterSupervisorType.Selected;
  }

  get isSupervisorSelected(): boolean {
    return isNotNil(this.supervisorControlValue);
  }

  get supervisorControlValue(): Maybe<ScheduleJobProgressUserAutocompleteItem> {
    return getControlValue(this.form.group, 'supervisor');
  }

  ngOnInit(): void {
    const { reportFilterConfig, printers } = getDialogInitData(this.dialogData);
    this.printerList = reportFilterConfig.printers;
    this.originalPrinterList = reportFilterConfig.printers;
    this.isSupplierPrinterSelectionActiveHash = reportFilterConfig.isSupplierPrinterSelectionActive;
    this.printerGridDefinition = this.buildPrinterGridDefinition();
    this.isCloud = !!printers.isCloud;

    this.form = this.formStateBuilder.buildPlain({
      mode: FormMode.CreateMode,
      enabled: whenCreateMode(),
      value: {
        supervisorType: this.scheduleJobProgressFilterSupervisorType.All,
        isCompleted: false,
        isFinalized: false,
        criticalPathCc: false,
        sendSuppliers: false,
        supervisor: this.isSystemRoleSupervisor()
          ? { userId: this.userContext.data.id, userName: this.userContext.data.fullName }
          : void 0,
      },
      children: {
        supervisorType: {
          visible: this.isSystemRoleMasterOrCorporateOrFranchisee(),
          enabled: this.isSystemRoleMasterOrCorporateOrFranchisee(),
        },
        supervisor: {
          visible: this.isAutocompleteControlExist(),
          enabled: false,
        },
        isCompleted: void 0,
        isFinalized: void 0,
        criticalPathCc: void 0,
        sendSuppliers: void 0,
        printerId: void 0,
      },
      confirmations: {
        skipSupplierDeliveryMethodCheck: false,
      },
    });
  }

  currentSupervisorName(): string {
    return this.userContext.data.fullName;
  }

  currentSupervisorId(): string {
    return this.userContext.data.id;
  }

  isSendSuppliersSelected(): boolean {
    return getControlValue(this.form.group, 'sendSuppliers') === true;
  }

  isSupervisorSectionCompleted(): boolean {
    return this.isAllSupervisorSelected() || (this.isSingleSupervisorSelected && this.isSupervisorSelected);
  }

  isJobProgressReportAllowed(): boolean {
    return this.userContext.hasPermission(BuilderPermission.Reports.Schedules.JOB_PROGRESS);
  }

  isSystemRoleMasterOrCorporateOrFranchisee(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasSystemRole(SystemRoleBit.Franchisee);
  }

  isAutocompleteControlExist(): boolean {
    return this.isSystemRoleMasterOrCorporateOrFranchisee() || this.isSystemRoleSupervisor();
  }

  isSystemRoleSupervisor(): boolean {
    return this.userContext.hasSystemRole(SystemRoleBit.Supervisor);
  }

  onSelectPrinter({ cloudPrinterId }: PrinterFinderListItem): void {
    setControlValue(this.form.group, 'printerId', cloudPrinterId);
  }

  isAllSupervisorSelected(): boolean {
    return this.isSystemRoleMasterOrCorporateOrFranchisee()
      && getControlValue(this.form.group, 'supervisorType')
      === ScheduleJobProgressFilterSupervisorType.All;
  }

  isPrinterSelected(): boolean {
    return !this.isCloud || isNotNil(getControlValue(this.form.group, 'printerId'));
  }

  isPrinterChecked({ cloudPrinterId }: PrinterFinderListItem): boolean {
    return eq(getControlValue(this.form.group, 'printerId'), cloudPrinterId);
  }

  isPreviewDisabled(): boolean {
    return !this.isJobProgressReportAllowed()
      || (this.isSystemRoleMasterOrCorporateOrFranchisee() && !this.isSupervisorSectionCompleted());
  }

  onSupervisorChange({ value }: MatRadioChange): void {
    const supervisorAutocompleteControl = getControl(this.form.group, 'supervisor');
    if (value === this.scheduleJobProgressFilterSupervisorType.All) {
      supervisorAutocompleteControl.disable();
    } else {
      supervisorAutocompleteControl.enable();
    }
  }

  onSearchPrinter(): void {
    const localizationHelper = this.localizationService.getStringL10nHelper();
    const name = this.printerName.value;
    this.printerList = name
      ? this.originalPrinterList.filter(({ cloudPrinterName }) =>
        localizationHelper.includesIgnoreCase(cloudPrinterName, name))
      : this.originalPrinterList;
  }

  onPreview(): void {
    this.reports.generateUrlAndShowDialog(REPORT_JOB_PROGRESS, jobProgressReportFilterToDto(this.composeSubmitValue()))
      .subscribe();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onDeliver(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.isCloud) {
      this.printerBusinessService.grantGoogleOfflineAccess().pipe(
        this.waitings.operator())
        .subscribe((isConnected: boolean) =>
                     this.dialogRef.close({ ...this.composeSubmitValue(), authCode: this.googleAuth.accessToken }));
    } else {
      this.dialogRef.close({ ...this.composeSubmitValue() });
    }
  }

  loadSupervisorSystemRoleUsers({ query, load }: AutocompleteLoadMoreEvent): void {
    setControlValue(this.form.group, 'supervisorType', this.scheduleJobProgressFilterSupervisorType.Selected);
    load(this.business.findAllSupervisors(query));
  }

  private composeSubmitValue(): ScheduleJobProgressFilter {
    return {
      ...this.form.submitValue,
      supervisorType: !this.isSystemRoleMasterOrCorporateOrFranchisee() && this.isSystemRoleSupervisor()
        ? this.scheduleJobProgressFilterSupervisorType.Selected
        : this.form.submitValue.supervisorType,
    };
  }

  private buildPrinterGridDefinition(): GridDefinition {
    return this.gridDefinitionFactory.buildManual([{
      field: 'cloudPrinterName',
      headerName: this.translations.get('lbl_printer_name'),
    }],                                           {
      getRowId: (params) => String(params.data.cloudPrinterId),
    });
  }
}

export const scheduleJobProgressFilterDialogRoute = createDialogRoute({
  component: ScheduleJobProgressFilterDialogComponent,
  init: {
    service: ScheduleListBusinessService,
    selector: 'initScheduleJobProgressFilterDialog',
  },
});
